import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { getBigAssetPath, getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import { unescapeTags } from '@aytm/lib/common/func'
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  EcommerceBannerPlacement,
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types.ts'
import { defaultAssetIndex } from '../../utils/defaultAssetIndex.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { getAssetType } from '../../utils/getAssetType.ts'
import AddToCartButton from './AddToCartButton.tsx'
import shippingIcon from '../assets/icons/shipping.svg'
import pickupIcon from '../assets/icons/pickup.svg'
import deliveryIcon from '../assets/icons/delivery.svg'
import cardsImage from '../assets/images/cards.jpg'
import AddedToCartModal from './AddedToCartModal.tsx'
import AssetsModal from './AssetsModal.tsx'
import BannerCard from './BannerCard.tsx'
import Breadcrumbs from './Breadcrumbs.tsx'
import Rating from './Rating.tsx'
import VariantSelect from './VariantSelect.tsx'
import style from './Product.module.scss'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'
import AssetsGate from '../../components/AssetsGate.tsx'

interface IProductProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  response: EcommerceResponse
  findability: boolean
  onItemClose: () => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onCartOpen: () => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
  scrollTop: () => void
}

// Deterministic 5..1-star share split whose weighted mean lands exactly on `rating` (clamped
// to the scale): a small fixed tail below the two adjacent buckets solved from the mean; the
// tail is dropped when it would put the mean out of reach, so the split is always total.
const deriveHistogram = (rating: number, reviews: number) => {
  const clamped = Math.min(5, Math.max(1, rating))
  let tail = clamped >= 3.5 ? [0, 0, 0.02, 0.01, 0.02] : [0, 0, 0, 0, 0]
  let tailMean = tail.reduce((sum, p, i) => sum + p * (5 - i), 0)
  let rest = 1 - tail.reduce((sum, p) => sum + p, 0)
  if (clamped > tailMean + rest * 5) {
    tail = [0, 0, 0, 0, 0]
    tailMean = 0
    rest = 1
  }
  const shares = [...tail]
  for (let top = 5; top >= 2; top--) {
    const high = clamped - tailMean - rest * (top - 1)
    if (high >= -1e-9 && high <= rest + 1e-9) {
      const capped = Math.min(rest, Math.max(0, high))
      shares[5 - top] += capped
      shares[5 - (top - 1)] += rest - capped
      break
    }
  }
  const roundToTotal = (values: number[], total: number) => {
    const floors = values.map(Math.floor)
    let left = Math.round(total - floors.reduce((sum, v) => sum + v, 0))
    values
      .map((value, index) => ({ index, frac: value - floors[index] }))
      .sort((a, b) => b.frac - a.frac)
      .forEach(({ index }) => left-- > 0 && (floors[index] += 1))
    return floors
  }
  const percents = roundToTotal(
    shares.map(share => share * 100),
    100
  )
  const counts = roundToTotal(
    shares.map(share => share * reviews),
    reviews
  )
  return shares.map((_, i) => ({ percent: percents[i], count: counts[i] }))
}

const Product = ({
  schema,
  answer,
  response,
  findability,
  onItemClose,
  onItemSelect,
  onCartOpen,
  onAddToCart,
  scrollTop
}: IProductProps) => {
  const timeline = useTimeline()
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()

  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  // A rating below one star is unreachable for a real product, so it is treated as no rating.
  const hasRating = Number(answer.rating) >= 1
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const discountOff = () => {
    const diff = answer.price - answer.discounted_price
    return schema.price_mask.replace('{{price}}', diff.toFixed(2))
  }
  const amount = response.answers[answer.index].v
  // "Members also considered" = featured top-level products, excluding the open one.
  const alsoConsidered = useMemo(
    () => schema.answers.filter(item => item.featured && !item.na && !item.parent_product && item.id !== answer.id),
    [schema.answers, answer]
  )
  const aboutHtml = [answer.description, answer.feature_bullets].filter(Boolean).join(' ')
  // Decorative shipping ETA: always today + 3 days (no randomness).
  const shippingDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(languageTag, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })

  const assetsCollection = useAssetsCollectionBuilder(answer)
  const hasVideos = useMemo(() => answer.gallery?.some(asset => 'video' in asset), [answer])

  const [position, setPosition] = useState<null | { x: number; y: number }>(null)
  const [onImage, setOnImage] = useState<boolean>(false)
  const [openAssetsModal, setOpenAssetsModal] = useState<boolean>(false)
  const [showAddedModal, setShowAddedModal] = useState<boolean>(false)
  const [aboutOpen, setAboutOpen] = useState<boolean>(true)
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(() => defaultAssetIndex(assetsCollection))

  // The gallery and the carousel each wait for their own photos; the buy box is there at once. The
  // gallery waits for the photo it opens on, not the selected one, so browsing never brings it back.
  const openingAsset = assetsCollection[defaultAssetIndex(assetsCollection)]
  const galleryImages = [
    openingAsset && ASSETS_TYPES.IMAGE === getAssetType(openingAsset) && getBigAssetPath(openingAsset as IImageAsset),
    ...assetsCollection.map(asset => {
      const type = getAssetType(asset)
      if (ASSETS_TYPES.IMAGE === type) return getSmallAssetPath(asset as IImageAsset)
      if (ASSETS_TYPES.VIDEO === type) return (asset as IEcommerceVideoAsset).video?.thumbnail
      return null
    })
  ]
  const alsoConsideredImages = alsoConsidered.map(item => item.image && getMediumAssetPath(item.image))

  // Close the open product first, or both stay `selected` and the view keeps the old one.
  const onVariantSelect = (index: number) => {
    onItemClose()
    onItemSelect(index)
  }

  const onAssetSelect = useCallback((index: number) => {
    setSelectedAssetIndex(index)
  }, [])
  const onMoveOnImage = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onImage) {
        timeline.log({ ref: answer.id, target: selectedAssetIndex.toString(), type: 'zoom_image_in' })
        setOnImage(true)
      }
      const element = event.currentTarget
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        const mouseX = event.clientX - left
        const mouseY = event.clientY - top
        setPosition({
          x: -(mouseX - width / 2) * 0.75,
          y: -(mouseY - height / 2) * 0.75
        })
      })
    },
    [onImage, timeline, selectedAssetIndex, answer.id]
  )
  const onImageEnter = () => {
    if (!onImage) {
      timeline.log({ ref: answer.id, target: selectedAssetIndex.toString(), type: 'zoom_image_in' })
    }
    setOnImage(true)
  }
  const resetImageState = () => {
    requestAnimationFrame(() => {
      if (ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) && onImage) {
        timeline.log({ ref: answer.id, target: selectedAssetIndex.toString(), type: 'zoom_image_out' })
      }
      setOnImage(false)
      setPosition({ x: 0, y: 0 })
    })
  }
  const slide = (qnt: -1 | 1) => {
    const nextIndex = selectedAssetIndex + qnt
    if (nextIndex >= 0 && nextIndex < assetsCollection.length) {
      setSelectedAssetIndex(nextIndex)
    }
  }
  const onAssetsModalOpen = (assetIndex: number) => {
    timeline.log({
      ref: answer.id,
      type: `open_${getAssetType(assetsCollection[assetIndex]).toLowerCase()}_dialog`
    })
    setSelectedAssetIndex(assetIndex)
    setOpenAssetsModal(true)
  }
  const onModalClose = (assetType: ASSETS_TYPES) => {
    timeline.log({
      ref: answer.id,
      type: `close_${assetType.toLowerCase()}_dialog`
    })
    setOpenAssetsModal(false)
  }

  useEffect(() => {
    scrollTop()
  }, [scrollTop])
  useEffect(() => {
    timeline.log({
      ref: answer.id,
      target: selectedAssetIndex.toString(),
      type: 'show_asset',
      context:
        ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAssetIndex])
          ? 'video'
          : ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex])
            ? 'image'
            : 'model'
    })
  }, [timeline, answer.id, selectedAssetIndex, assetsCollection])

  // No hover on touch: thumbs select on tap there, on hover on desktop.
  const thumbSelectProps = (index: number) =>
    viewport.mobile ? { onClick: () => onAssetSelect(index) } : { onMouseEnter: () => onAssetSelect(index) }

  const renderThumb = (asset: assetsCollectionElement, index: number) => (
    <>
      {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
        <div
          {...thumbSelectProps(index)}
          className={`${style.thumb} ${index === selectedAssetIndex ? style.thumbActive : ''}`}
          role="img"
          aria-label={(asset as IImageAsset)?.alt_text || t('questions', 'img', 'noAlt')}
          style={{ backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})` }}
        />
      )}
      {ASSETS_TYPES.MODEL === getAssetType(asset) && (
        <div className={`${style.thumbModelWrapper} ${index === selectedAssetIndex ? style.thumbActive : ''}`}>
          <model-viewer
            src={(asset as IFileAsset).file_url}
            camera-controls={false}
            interaction-prompt="none"
            style={{ width: '64px', height: '64px' }}
          />
          <div {...thumbSelectProps(index)} className={style.thumbModelOverlay} />
        </div>
      )}
      {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
        <div
          className={`${style.thumbVideo} ${index === selectedAssetIndex ? style.thumbActive : ''}`}
          {...thumbSelectProps(index)}
          role="presentation"
          style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video?.thumbnail})` }}
        >
          <i className="ld-Play" />
        </div>
      )}
    </>
  )

  const histogram = hasRating ? deriveHistogram(answer.rating, answer.reviews || 0) : []

  const assetsModal = openAssetsModal && (
    <AssetsModal
      answer={answer}
      assetsCollection={assetsCollection}
      currentIndex={selectedAssetIndex}
      onModalClose={onModalClose}
    />
  )

  // The strip of thumbnails; its skeleton fills the same slots with grey bars.
  const thumbStrip = (renderItem: (asset: assetsCollectionElement, index: number) => ReactNode) =>
    assetsCollection.length > 0 && (
      <div className={`${style.thumbs} ${viewport.mobile ? style.thumbsMobile : ''}`}>
        <div className={style.thumbsInner}>
          {assetsCollection.map((asset, index) => (
            <div className={style.thumbItem} key={index}>
              {renderItem(asset, index)}
            </div>
          ))}
        </div>
      </div>
    )
  const thumbs = thumbStrip(renderThumb)
  const thumbsSkeleton = thumbStrip(() => <div className={style.thumbSkeleton} />)

  const selectedAsset = assetsCollection[selectedAssetIndex]
  const mainAsset = assetsCollection.length ? (
    <>
      {ASSETS_TYPES.IMAGE === getAssetType(selectedAsset) &&
        (viewport.mobile ? (
          // Flat on touch (no hover zoom); a tap opens the fullscreen viewer.
          <div
            className={style.mainImage}
            role="img"
            aria-label={(selectedAsset as IImageAsset)?.alt_text || t('questions', 'img', 'noAlt')}
            style={{ backgroundImage: `url(${getBigAssetPath(selectedAsset as IImageAsset)})` }}
            onClick={() => onAssetsModalOpen(selectedAssetIndex)}
          />
        ) : (
          <div
            className={style.mainImage}
            role="img"
            aria-label={(selectedAsset as IImageAsset)?.alt_text || t('questions', 'img', 'noAlt')}
            style={{
              backgroundImage: `url(${getBigAssetPath(selectedAsset as IImageAsset)})`,
              backgroundPosition: position ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)` : '50% 50%',
              transform: `scale(${onImage ? 2 : 1})`
            }}
            onMouseMove={onMoveOnImage}
            onMouseEnter={onImageEnter}
            onMouseLeave={resetImageState}
          />
        ))}
      {ASSETS_TYPES.MODEL === getAssetType(selectedAsset) && (
        <div className={style.viewerWrapper}>
          {/* rotate-only: a drag ends in a click, so the fullscreen viewer opens from the zoom button instead */}
          <model-viewer
            style={{ height: '100%' }}
            src={(selectedAsset as IFileAsset).file_url}
            ar
            camera-controls
            touch-action="pan-y"
          />
        </div>
      )}
      {ASSETS_TYPES.VIDEO === getAssetType(selectedAsset) && (
        <div
          className={style.mainVideo}
          onClick={() => onAssetsModalOpen(selectedAssetIndex)}
          role="presentation"
          style={{ backgroundImage: `url(${(selectedAsset as IEcommerceVideoAsset).video?.thumbnail})` }}
        >
          <div className={style.playWrapper}>
            <i className="ld-Play" />
          </div>
        </div>
      )}
    </>
  ) : (
    <div className={style.noImage} />
  )

  const imageActions = (
    <div className={`${style.imageActions} ${viewport.mobile ? style.imageActionsMobile : ''}`}>
      <EmptyLink color="#0a6cff">
        <span className={style.iconWrapper}>
          <i className="ld-Share" />
        </span>
      </EmptyLink>
      {viewport.mobile && (
        <EmptyLink color="#0a6cff">
          <span className={style.iconWrapper} role="img" aria-label={t('questions', 'ec', 'samsclub', 'saveForLater')}>
            <i className="ld-Heart" />
          </span>
        </EmptyLink>
      )}
      {selectedAsset && (
        <button
          type="button"
          className={style.iconWrapper}
          disabled={ASSETS_TYPES.VIDEO === getAssetType(selectedAsset)}
          onClick={() => onAssetsModalOpen(selectedAssetIndex)}
        >
          <i className="ld-ZoomIn" />
        </button>
      )}
      {hasVideos && (
        <button
          type="button"
          className={style.iconWrapper}
          disabled={ASSETS_TYPES.VIDEO !== getAssetType(selectedAsset)}
          onClick={() => onAssetsModalOpen(selectedAssetIndex)}
        >
          <i className="ld-Play" />
        </button>
      )}
    </div>
  )

  const brand = answer.sub_title && (
    <EmptyLink color="#0a6cff">
      <span className={style.brand} dangerouslySetInnerHTML={{ __html: unescapeTags(answer.sub_title) }} />
    </EmptyLink>
  )

  const badges = (hasDiscount || answer.red_label || answer.green_label) && (
    <div className={style.badges}>
      {hasDiscount && (
        <span className={style.instantSavings}>{t('questions', 'ec', 'samsclub', 'instantSavings')}</span>
      )}
      {answer.red_label && <span className={style.redLabel} dangerouslySetInnerHTML={{ __html: answer.red_label }} />}
      {answer.green_label && (
        <span className={style.greenLabel} dangerouslySetInnerHTML={{ __html: answer.green_label }} />
      )}
    </div>
  )

  const title = <h1 className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />

  const itemNumber = answer.market_place_id && (
    <span className={style.itemNumber}>
      {t<(id: string) => string>('questions', 'ec', 'samsclub', 'itemNumber')(answer.market_place_id)}
    </span>
  )

  const ratingStars = hasRating && (
    <>
      <Rating rating={answer.rating} />
      <span className={style.ratingNumeric}>({answer.rating})</span>
      {answer.reviews != null && (
        <EmptyLink color="#0a6cff">
          <span className={style.reviewsLink}>
            {t<(count: string) => string>(
              'questions',
              'ec',
              'samsclub',
              'ratingsCount'
            )(answer.reviews.toLocaleString(languageTag))}
          </span>
        </EmptyLink>
      )}
    </>
  )

  const priceBlock = (
    <div className={style.priceBlock}>
      <div className={style.priceRow}>
        {hasDiscount ? (
          <>
            <span className={style.now}>{t('questions', 'ec', 'samsclub', 'nowPrice')}</span>
            <span className={style.priceSale} dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }} />
            <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
          </>
        ) : (
          <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
        )}
      </div>
      {answer.per_count_price && (
        <div className={style.unitPrice} dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }} />
      )}
      {hasDiscount && (
        <div className={style.saveOff}>
          {t<(amount: string) => string>('questions', 'ec', 'samsclub', 'saveOff')(discountOff())}
        </div>
      )}
      {schema.max_qty_per_product > 0 && (
        <div className={style.limit}>
          {t<(limit: number) => string>('questions', 'ec', 'samsclub', 'limitOf')(schema.max_qty_per_product)}
        </div>
      )}
      <div className={style.pricesNote}>
        {t('questions', 'ec', 'samsclub', 'pricesMayVary')}
        <EmptyLink color="#0a6cff">
          <i className="ld-InfoCircle" />
        </EmptyLink>
      </div>
    </div>
  )

  const variantSelect = <VariantSelect schema={schema} answer={answer} onSelect={onVariantSelect} />

  const fulfilmentTiles = (
    <div className={style.fulfilmentTiles}>
      <div className={`${style.tile} ${style.tileActive}`}>
        <img src={shippingIcon} alt="" role="presentation" className={style.tileIcon} />
        <span className={style.tileTitle}>{t('questions', 'ec', 'samsclub', 'chipShipping')}</span>
        <span className={style.tileNote}>
          {t<(date: string) => string>('questions', 'ec', 'samsclub', 'arrives')(shippingDate)}
        </span>
      </div>
      <div className={style.tile}>
        <img src={pickupIcon} alt="" role="presentation" className={style.tileIcon} />
        <span className={style.tileTitle}>{t('questions', 'ec', 'samsclub', 'chipPickup')}</span>
        <span className={style.tileNote}>{t('questions', 'ec', 'samsclub', 'notAvailable')}</span>
      </div>
      <div className={style.tile}>
        <img src={deliveryIcon} alt="" role="presentation" className={style.tileIcon} />
        <span className={style.tileTitle}>{t('questions', 'ec', 'samsclub', 'chipDelivery')}</span>
        <span className={style.tileNote}>{t('questions', 'ec', 'samsclub', 'notAvailable')}</span>
      </div>
    </div>
  )

  const shippingBlock = (
    <div className={style.shippingBlock}>
      <div className={style.shippingTitle}>{t('questions', 'ec', 'samsclub', 'shippingTitle')}</div>
      <div className={style.shippingRow}>
        <span dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'samsclub', 'freeForPlus') }} />
        <EmptyLink color="#0a6cff">
          <span className={style.inlineLink}>{t('questions', 'ec', 'samsclub', 'upgrade')}</span>
        </EmptyLink>
      </div>
      <div className={style.shippingRow}>
        <span className={style.addressLine}>{t('questions', 'ec', 'samsclub', 'locationLine')}</span>
        <EmptyLink color="#0a6cff">
          <span className={style.inlineLink}>{t('questions', 'ec', 'samsclub', 'changeAddress')}</span>
        </EmptyLink>
      </div>
    </div>
  )

  // On touch the first add (0 -> 1) confirms in a sheet, as the app does; findability has no cart to confirm.
  const onBuyBoxAdd = (qnt: number) => {
    const firstAdd = qnt > 0 && 0 === amount
    onAddToCart(answer.index, qnt)
    if (viewport.mobile && firstAdd && !findability) setShowAddedModal(true)
  }

  const addToCart = (
    <AddToCartButton
      amount={amount}
      findability={findability}
      limit={schema.max_qty_per_product}
      spread
      onAdd={onBuyBoxAdd}
    />
  )

  const serviceRows = (
    <>
      <div className={style.serviceRow}>
        <i className="ld-Returns" />
        <span>{t('questions', 'ec', 'samsclub', 'returnAnytime')}</span>
        <EmptyLink color="#0a6cff">
          <span className={style.inlineLink}>{t('questions', 'ec', 'samsclub', 'seeDetails')}</span>
        </EmptyLink>
      </div>
      <div className={style.serviceRow}>
        <i className="ld-CreditCard" />
        <span>{t('questions', 'ec', 'samsclub', 'snapEbt')}</span>
      </div>
      <div className={style.serviceRow}>
        <EmptyLink color="#0a6cff">
          <span className={style.addToList}>
            <i className="ld-AddToList" />
            {t('questions', 'ec', 'samsclub', 'addToList')}
          </span>
        </EmptyLink>
      </div>
    </>
  )

  const creditCard = (
    <div className={`${style.creditCard} ${viewport.mobile ? style.creditCardMobile : ''}`}>
      <img src={cardsImage} alt="" role="presentation" className={style.creditImage} />
      <span className={style.creditText}>{t('questions', 'ec', 'samsclub', 'creditOffer')}</span>
      <EmptyLink color="#0a6cff">
        <span className={style.applyNow}>{t('questions', 'ec', 'samsclub', 'applyNow')}</span>
      </EmptyLink>
    </div>
  )

  const alsoConsideredSection = alsoConsidered.length > 0 && (
    <div className={style.alsoConsidered}>
      <h2 className={style.sectionTitle}>{t('questions', 'ec', 'samsclub', 'membersAlsoConsidered')}</h2>
      <AssetsGate
        urls={alsoConsideredImages}
        loader={
          <div className={`${style.alsoConsideredRow} ${style.alsoConsideredLoading}`}>
            {alsoConsidered.map(item => (
              <div key={item.id} className={`${style.cardSkeleton} ${viewport.mobile ? style.cardSkeletonMobile : ''}`}>
                <div className={style.cardSkeletonImage} />
                <div className={style.cardSkeletonCta} />
                <div className={style.cardSkeletonPrice} />
                <div className={style.cardSkeletonLine} />
                <div className={`${style.cardSkeletonLine} ${style.short}`} />
              </div>
            ))}
          </div>
        }
      >
        <div className={style.alsoConsideredRow}>
          {alsoConsidered.map(item => (
            <BannerCard
              key={item.id}
              schema={schema}
              answer={item}
              amount={response.answers[item.index].v}
              findability={findability}
              disabled={'focus' == schema.mode_type && !item.focused}
              variant="recommendation"
              onSelect={() => {
                onItemClose()
                onItemSelect(item.index, 'featured')
              }}
              onAdd={qnt => onAddToCart(item.index, qnt, true)}
            />
          ))}
        </div>
      </AssetsGate>
    </div>
  )

  const aboutSection = aboutHtml && (
    <div className={style.about}>
      <h2 className={style.sectionTitle}>{t('questions', 'ec', 'samsclub', 'aboutTitle')}</h2>
      <button type="button" className={style.aboutToggle} onClick={() => setAboutOpen(!aboutOpen)}>
        {t('questions', 'ec', 'samsclub', 'productDetails')}
        <i className={aboutOpen ? 'ld-ChevronUp' : 'ld-ChevronDown'} />
      </button>
      {aboutOpen && (
        <>
          <div className={style.aboutContent} dangerouslySetInnerHTML={{ __html: unescapeTags(aboutHtml) }} />
          <div className={`${style.reportRow} ${viewport.mobile ? style.reportRowMobile : ''}`}>
            <span className={style.reportNote}>
              <EmptyLink color="#0a6cff">
                <i className={`ld-InfoCircle ${style.reportNoteIcon}`} />
              </EmptyLink>
              <span>{t('questions', 'ec', 'samsclub', 'reportIncorrectNote')}</span>
            </span>
            <EmptyLink color="#0a6cff">
              <span className={style.inlineLink}>{t('questions', 'ec', 'samsclub', 'reportIncorrect')}</span>
            </EmptyLink>
          </div>
        </>
      )}
    </div>
  )

  const reviewsSection = hasRating && (
    <div className={style.reviews}>
      <h2 className={style.sectionTitle}>{t('questions', 'ec', 'samsclub', 'reviewsTitle')}</h2>
      <div className={style.reviewsColumns}>
        <div className={style.reviewsSummary}>
          <div className={style.reviewsScore}>
            {t<(rating: number) => string>('questions', 'ec', 'samsclub', 'outOfFive')(answer.rating)}
          </div>
          <div className={style.reviewsStars}>
            <Rating rating={answer.rating} />
            {answer.reviews != null && (
              <span className={style.reviewsCount}>
                {t<(count: string) => string>(
                  'questions',
                  'ec',
                  'samsclub',
                  'ratingsCount'
                )(answer.reviews.toLocaleString(languageTag))}
              </span>
            )}
          </div>
          <div className={style.reviewsButtons}>
            <EmptyLink color="#0a6cff">
              <span className={style.reviewsOutlineButton}>{t('questions', 'ec', 'samsclub', 'viewAllReviews')}</span>
            </EmptyLink>
            <EmptyLink color="#0a6cff">
              <span className={style.reviewsFillButton}>{t('questions', 'ec', 'samsclub', 'writeReview')}</span>
            </EmptyLink>
          </div>
        </div>
        <div className={style.histogram}>
          {histogram.map(({ percent, count }, i) => (
            <div className={style.histogramRow} key={i}>
              <EmptyLink color="#0a6cff">
                <span className={style.histogramLabel}>
                  {t<(stars: number) => string>('questions', 'ec', 'samsclub', 'starsRow')(5 - i)}
                </span>
              </EmptyLink>
              <span className={style.histogramTrack}>
                <span className={style.histogramBar} style={{ width: `${percent}%` }} />
              </span>
              <span className={style.histogramValue}>
                {percent}% ({count.toLocaleString(languageTag)})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  if (viewport.mobile) {
    return (
      <section className={`${style.product} ${style.productMobile}`} data-testid="SW-EC-PDP">
        {assetsModal}
        {showAddedModal && (
          <AddedToCartModal
            schema={schema}
            answer={answer}
            response={response}
            findability={findability}
            onItemClose={onItemClose}
            onItemSelect={onItemSelect}
            onCartOpen={onCartOpen}
            onAddToCart={onAddToCart}
            onModalClose={() => setShowAddedModal(false)}
          />
        )}

        <div className={style.mobileHead}>
          {brand}
          {badges}
          {title}
          {itemNumber}
          {hasRating && <div className={style.ratingRow}>{ratingStars}</div>}
        </div>

        <AssetsGate
          urls={galleryImages}
          loader={
            <div className={`${style.mobileGallery} ${style.galleryLoading}`}>
              <div className={style.mobileMain}>
                <div className={style.mainSkeleton} />
                {imageActions}
              </div>
              {thumbsSkeleton}
            </div>
          }
        >
          <div className={style.mobileGallery}>
            <div className={style.mobileMain}>
              {mainAsset}
              {imageActions}
            </div>
            {thumbs}
          </div>
        </AssetsGate>

        <div className={style.mobileBuyBox}>
          {priceBlock}
          {variantSelect}
          {fulfilmentTiles}
          {shippingBlock}
          {serviceRows}
          {creditCard}
        </div>

        {alsoConsideredSection}
        {aboutSection}
        {reviewsSection}

        {/* The buy control lives in a bar pinned above the tab bar, not in the buy box. */}
        <div className={style.stickyBar} data-testid="SW-EC-PDP-AddToCartSidebar">
          <span
            className={style.stickyPrice}
            dangerouslySetInnerHTML={{ __html: price(hasDiscount ? answer.discounted_price : answer.price) }}
          />
          <div className={style.stickyCta}>{addToCart}</div>
        </div>
      </section>
    )
  }

  return (
    <section className={style.product} data-testid="SW-EC-PDP">
      {assetsModal}

      <Breadcrumbs schema={schema} current={answer.text} onBack={onItemClose} />

      <div className={style.columns}>
        <AssetsGate
          urls={galleryImages}
          loader={
            <div className={`${style.gallery} ${style.galleryLoading}`}>
              {thumbsSkeleton}
              <div className={style.mainArea}>
                <span className={style.arrowSlot} />
                <div className={style.bigImage}>
                  <div className={style.mainSkeleton} />
                </div>
                <span className={style.arrowSlot} />
                {imageActions}
              </div>
            </div>
          }
        >
          <div className={style.gallery}>
            {thumbs}

            <div className={style.mainArea}>
              <span className={style.arrowSlot}>
                {selectedAssetIndex > 0 && (
                  <button type="button" className={style.arrow} onClick={() => slide(-1)}>
                    <i className="ld-ChevronLeft" />
                  </button>
                )}
              </span>
              <div className={style.bigImage}>{mainAsset}</div>
              <span className={style.arrowSlot}>
                {selectedAssetIndex < assetsCollection.length - 1 && (
                  <button type="button" className={style.arrow} onClick={() => slide(1)}>
                    <i className="ld-ChevronRight" />
                  </button>
                )}
              </span>
              {imageActions}
            </div>
          </div>
        </AssetsGate>

        <div className={style.buyBox}>
          <div className={style.buyBoxCard}>
            {brand}
            {badges}
            {title}
            {(hasRating || answer.market_place_id) && (
              <div className={style.ratingRow}>
                {ratingStars}
                {itemNumber}
              </div>
            )}

            {priceBlock}

            {variantSelect}

            {fulfilmentTiles}

            {shippingBlock}

            <div className={style.cta}>{addToCart}</div>

            {serviceRows}
          </div>

          {creditCard}
        </div>
      </div>

      {alsoConsideredSection}

      {aboutSection}

      {reviewsSection}
    </section>
  )
}

export default Product
